import React, { useState, useCallback } from "react";
import { DropZone, Button } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { setModalxComponent, setDestroy } from "components/modalx/modalx.slice";
import { upload, replaceFile, isEmptyUploadError } from "services/upload-file.service";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchFinanceDetailRefreshAsync } from "containers/finance-detail/finance-detail-container.slice";
import { useAppDispatch } from "hooks";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { signerEditErrorKey } from "utils/signer-edit-errors";

interface ReplaceFileProps {
  title?: string;
  onChange: () => void;
  subject?: string;
  parentFileId?: string | null;
  year?: number;
  financeId?: string;
}

/**
 * Uploads a manually signed copy and puts it in place of the document
 * `parentFileId` (the backend points that document at the new file). Calls
 * `onChange` once the replacement is saved, so the caller can refresh.
 */
export function useUploadSignedCopy({
  parentFileId = null,
  year,
  financeId,
  onChange,
}: Pick<ReplaceFileProps, "parentFileId" | "year" | "financeId" | "onChange">) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId, customerId, orderId } = useParams<{
    companyId: string;
    customerId: string;
    orderId: string;
  }>();

  return useCallback(
    async (file: File) => {
      try {
        dispatch(setModalxComponent("Loading"));
        const { data } = await upload(file, companyId);
        const payload = {
          prevId: parentFileId,
          nextId: data,
          customerId,
        };
        await replaceFile(companyId, payload);
        if (companyId && customerId)
          dispatch(fetchCustomer({ companyId, customerId }));

        // Refresh finance detail if year is provided
        if (companyId && customerId && year) {
          dispatch(fetchFinanceDetailRefreshAsync({
            companyId,
            customerId,
            year,
            financeId,
          }));
        }

        if (companyId && orderId)
          dispatch(fetchOrderDetails({ companyId, orderId }));
        dispatch(setDestroy());
        onChange();
      } catch (error: any) {
        // Close the Loading modal so it never hangs open, and say why. An
        // empty file was already toasted by upload() — no second message.
        console.error(error);
        dispatch(setDestroy());
        if (!isEmptyUploadError(error)) {
          toast.error(t(signerEditErrorKey(error, "file_upload_failed")));
        }
      }
    },
    [companyId, dispatch, parentFileId, customerId, orderId, year, financeId, onChange, t]
  );
}

const ReplaceFile: React.FC<ReplaceFileProps> = ({
  title = "upload",
  onChange,
  parentFileId,
  year,
  financeId,
}) => {
  const uploadSignedCopy = useUploadSignedCopy({
    parentFileId,
    year,
    financeId,
    onChange,
  });

  const [openFileDialog, setOpenFileDialog] = useState(false);
  const { t } = useTranslation();

  const handleDropZoneDrop = useCallback(
    (acceptedFiles: File[]) => {
      // The drop zone takes a single file (allowMultiple={false}).
      if (acceptedFiles.length === 1) uploadSignedCopy(acceptedFiles[0]);
    },
    [uploadSignedCopy]
  );

  const toggleOpenFileDialog = useCallback(() => {
    setOpenFileDialog((prev) => !prev);
  }, []);

  return (
    <>
      <Button variant="primary" tone="critical" onClick={toggleOpenFileDialog}>
        {t(title)}
      </Button>
      <div style={{ display: "none" }}>
        <DropZone
          allowMultiple={false}
          openFileDialog={openFileDialog}
          onDropAccepted={handleDropZoneDrop}
          onFileDialogClose={toggleOpenFileDialog}
        >
          <DropZone.FileUpload />
        </DropZone>
      </div>
    </>
  );
};

export default ReplaceFile;
